import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  FilePlus2, Download, Type, Hash, Calendar, Loader2, CheckCircle2, FileText, AlertCircle,
  Clock, Timer, ToggleLeft, AlignLeft, Image
} from "lucide-react";
import { toast } from "sonner";

export default function GenerateDocumentPage() {
  const { authHeaders, token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  // Load templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/templates`, authHeaders());
        setTemplates(res.data);
      } catch {
        toast.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Load selected template details
  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplate(null);
      setFormValues({});
      return;
    }
    const fetchTemplate = async () => {
      try {
        const res = await axios.get(`${API}/templates/${selectedTemplateId}`, authHeaders());
        setSelectedTemplate(res.data);
        // Initialize form values
        const initial = {};
        (res.data.variables || []).forEach((v) => {
          initial[v.name] = "";
        });
        setFormValues(initial);
        setLastGenerated(null);
      } catch {
        toast.error("Failed to load template details");
      }
    };
    fetchTemplate();
  }, [selectedTemplateId]);

  const handleValueChange = (varName, value) => {
    setFormValues((prev) => ({ ...prev, [varName]: value }));
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;
    setGenerating(true);
    setLastGenerated(null);
    try {
      const res = await axios.post(
        `${API}/webhook/generate`,
        {
          template_id: selectedTemplateId,
          variables: formValues,
        },
        { responseType: "blob" }
      );

      const docId = res.headers["x-document-id"] || "unknown";

      setLastGenerated({ docId, timestamp: new Date() });

      // Auto-download the generated PDF using direct link
      if (docId !== "unknown") {
        const downloadUrl = `${API}/documents/${docId}/download?token=${encodeURIComponent(token)}`;
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = downloadUrl;
        a.download = `generated_${docId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => document.body.removeChild(a), 200);
      }

      toast.success("PDF generated successfully");
    } catch (err) {
      const text = err.response?.data ? await err.response.data.text() : null;
      let message = "Generation failed";
      if (text) {
        try {
          message = JSON.parse(text).detail || message;
        } catch {}
      }
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!lastGenerated?.docId) return;
    const downloadUrl = `${API}/documents/${lastGenerated.docId}/download?token=${encodeURIComponent(token)}`;
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = downloadUrl;
    a.download = `generated_${lastGenerated.docId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 200);
  };

  const handleClearForm = () => {
    const cleared = {};
    Object.keys(formValues).forEach((k) => { cleared[k] = ""; });
    setFormValues(cleared);
    setLastGenerated(null);
  };

  const typeIcon = (type) => {
    if (type === "number") return <Hash size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "date") return <Calendar size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "time") return <Clock size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "timestamp") return <Timer size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "boolean") return <ToggleLeft size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "textarea") return <AlignLeft size={14} className="text-slate-400" strokeWidth={1.5} />;
    if (type === "image") return <Image size={14} className="text-slate-400" strokeWidth={1.5} />;
    return <Type size={14} className="text-slate-400" strokeWidth={1.5} />;
  };

  const getInputType = (varType) => {
    if (varType === "number") return "number";
    if (varType === "date") return "date";
    if (varType === "time") return "time";
    if (varType === "timestamp") return "datetime-local";
    return "text";
  };

  const filledCount = Object.entries(formValues).filter(([k, v]) => {
    const varDef = selectedTemplate?.variables?.find(vd => vd.name === k);
    if (varDef?.type === "boolean") return v === "true" || v === "false";
    return v !== "";
  }).length;
  const totalVars = selectedTemplate?.variables?.length || 0;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto p-6 lg:p-8" data-testid="generate-document-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Generate Document
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Select a template, fill in the variables, and generate a PDF
          </p>
        </div>

        {/* Template Selection */}
        <Card className="mb-6" data-testid="template-selection-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <FileText size={14} strokeWidth={1.5} />
              Select Template
            </CardTitle>
            <CardDescription>Choose which template to generate a document from</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" />
                Loading templates...
              </div>
            ) : templates.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <AlertCircle size={14} />
                No templates found. Upload a template first.
              </div>
            ) : (
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger data-testid="template-select-trigger" className="w-full">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                      <span className="flex items-center gap-2">
                        {t.name}
                        <span className="text-xs text-slate-400">
                          ({t.variables?.length || 0} variables)
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>

        {/* Variable Form */}
        {selectedTemplate && (
          <>
            <Card className="mb-6" data-testid="variables-form-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <FilePlus2 size={14} strokeWidth={1.5} />
                      Fill Variables
                    </CardTitle>
                    <CardDescription>
                      {totalVars === 0
                        ? "This template has no variables defined"
                        : `${filledCount} of ${totalVars} fields filled`}
                    </CardDescription>
                  </div>
                  {totalVars > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={handleClearForm}
                      data-testid="clear-form-button"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                {totalVars > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-300"
                        style={{ width: `${totalVars > 0 ? (filledCount / totalVars) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {totalVars === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    No variables have been defined for this template. Open the template editor to add variables.
                  </p>
                ) : (
                  <div className="space-y-4" data-testid="variables-form-fields">
                    {selectedTemplate.variables.map((v) => (
                      <div key={v.id || v.name} className="space-y-1.5">
                        <Label
                          htmlFor={`var-${v.name}`}
                          className="flex items-center gap-1.5 text-sm"
                        >
                          {typeIcon(v.type)}
                          <span className="font-mono text-xs font-medium">{v.name}</span>
                          <span className="text-[10px] text-slate-400 ml-1 font-normal capitalize">
                            {v.type}
                          </span>
                        </Label>
                        {v.type === "boolean" ? (
                          <div className="flex items-center gap-3 pt-1">
                            <Switch
                              id={`var-${v.name}`}
                              checked={formValues[v.name] === "true"}
                              onCheckedChange={(checked) => handleValueChange(v.name, checked ? "true" : "false")}
                              data-testid={`var-input-${v.name}`}
                            />
                            <span className="text-sm text-slate-500">
                              {formValues[v.name] === "true" ? "Yes" : "No"}
                            </span>
                          </div>
                        ) : v.type === "textarea" ? (
                          <Textarea
                            id={`var-${v.name}`}
                            value={formValues[v.name] || ""}
                            onChange={(e) => handleValueChange(v.name, e.target.value)}
                            placeholder={`Enter ${v.name}...`}
                            rows={3}
                            className="text-sm"
                            data-testid={`var-input-${v.name}`}
                          />
                        ) : v.type === "image" ? (
                          <div className="space-y-1.5">
                            <Input
                              id={`var-${v.name}`}
                              type="url"
                              value={formValues[v.name] || ""}
                              onChange={(e) => handleValueChange(v.name, e.target.value)}
                              placeholder="https://example.com/image.png"
                              className="h-9"
                              data-testid={`var-input-${v.name}`}
                            />
                            <p className="text-[10px] text-slate-400">
                              Provide a publicly accessible URL to an image (PNG, JPG, etc.)
                            </p>
                            {formValues[v.name] && (
                              <div className="mt-1 p-2 bg-slate-50 rounded border border-slate-100">
                                <img
                                  src={formValues[v.name]}
                                  alt="Preview"
                                  className="max-h-16 object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <Input
                            id={`var-${v.name}`}
                            type={getInputType(v.type)}
                            value={formValues[v.name] || ""}
                            onChange={(e) => handleValueChange(v.name, e.target.value)}
                            placeholder={`Enter ${v.name}...`}
                            className="h-9"
                            data-testid={`var-input-${v.name}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generate Actions */}
            <Card data-testid="generate-actions-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleGenerate}
                    disabled={generating || totalVars === 0}
                    className="gap-2 flex-1 sm:flex-none"
                    data-testid="generate-pdf-button"
                  >
                    {generating ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FilePlus2 size={14} strokeWidth={1.5} />
                        Generate PDF
                      </>
                    )}
                  </Button>

                  {lastGenerated && (
                    <Button
                      variant="secondary"
                      onClick={handleDownload}
                      className="gap-2"
                      data-testid="download-generated-button"
                    >
                      <Download size={14} strokeWidth={1.5} />
                      Download PDF
                    </Button>
                  )}
                </div>

                {lastGenerated && (
                  <div className="mt-3 flex items-center gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200" data-testid="generation-success">
                    <CheckCircle2 size={16} className="text-emerald-600" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">PDF Generated Successfully</p>
                      <p className="text-xs text-emerald-600">
                        Document ID: <span className="font-mono">{lastGenerated.docId?.slice(0, 8) || "—"}</span>
                        {" | "}
                        {lastGenerated.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}
